import {
    collection,
    getDocs,
    query,
    where,
    orderBy,
    limit,
    startAfter,
    Timestamp,
    QueryDocumentSnapshot,
    QueryConstraint,
} from 'firebase/firestore'
import { firestore } from '../config/firebase'
import { AuditLog, AuditLogFilters } from '../../../shared/types/audit.types'
import { COLLECTIONS } from '../../../shared/constants/collections'

// Cursor type — exported so the page component can type its state
export type AuditLogCursor = QueryDocumentSnapshot

export interface AuditLogListOptions {
    limit?: number
    startAfter?: AuditLogCursor
}

export interface AuditLogPage {
    items: AuditLog[]
    cursor: AuditLogCursor | null
    hasMore: boolean
}

interface IUseAuditLogReturn {
    getList: (filters: AuditLogFilters, options?: AuditLogListOptions) => Promise<AuditLogPage>
}

const COLLECTION = COLLECTIONS.AUDIT_LOG

const convertDoc = (id: string, data: Record<string, unknown>): AuditLog => ({
    id,
    ...data,
    timestamp: data.timestamp instanceof Timestamp
        ? data.timestamp.toDate()
        : new Date(data.timestamp as string),
} as AuditLog)

const useAuditLog = (): IUseAuditLogReturn => {

    /**
     * Načte stránku audit log záznamů dle filtrů s kurzorovou paginací.
     *
     * Podporované kombinace filtrů a potřebné Firestore composite indexy:
     *   - pouze timestamp range  → single-field index (automatický)
     *   - entityType + timestamp → composite index (entityType ASC, timestamp DESC)
     *   - entityId + timestamp   → composite index (entityId ASC, timestamp DESC)
     *   - actorUid + timestamp   → composite index (actorUid ASC, timestamp DESC)
     *
     * Při chybějícím indexu Firestore vypíše odkaz pro jeho vytvoření do konzole.
     *
     * @param filters  - Volitelné filtry (entityType, entityId, actorUid, dateFrom, dateTo)
     * @param options  - Limit (výchozí 50) a startAfter kurzor pro stránkování
     */
    const getList = async (
        filters: AuditLogFilters,
        options?: AuditLogListOptions,
    ): Promise<AuditLogPage> => {
        const PAGE_SIZE = options?.limit ?? 50
        const constraints: QueryConstraint[] = []

        // Equality filters must come before orderBy
        if (filters.entityType) {
            constraints.push(where('entityType', '==', filters.entityType))
        }
        if (filters.entityId) {
            constraints.push(where('entityId', '==', filters.entityId))
        }
        if (filters.actorUid) {
            constraints.push(where('actorUid', '==', filters.actorUid))
        }

        // Timestamp range — both inequalities on the same field, compatible with orderBy below
        if (filters.dateFrom) {
            constraints.push(where('timestamp', '>=', Timestamp.fromDate(filters.dateFrom)))
        }
        if (filters.dateTo) {
            constraints.push(where('timestamp', '<=', Timestamp.fromDate(filters.dateTo)))
        }

        constraints.push(orderBy('timestamp', 'desc'))

        if (options?.startAfter) {
            constraints.push(startAfter(options.startAfter))
        }

        // Fetch one extra to detect whether another page exists
        constraints.push(limit(PAGE_SIZE + 1))

        const snap = await getDocs(query(collection(firestore, COLLECTION), ...constraints))

        const hasMore = snap.docs.length > PAGE_SIZE
        const docs = hasMore ? snap.docs.slice(0, PAGE_SIZE) : snap.docs

        return {
            items: docs.map(d => convertDoc(d.id, d.data())),
            cursor: docs.length > 0 ? docs[docs.length - 1] : null,
            hasMore,
        }
    }

    return { getList }
}

export default useAuditLog
